import React from 'react'
import { Box } from '@mui/material'

import Txt from './Txt'
import Button from './Button'
import CheckBox from './CheckBox'
import SelectField from './SelectField'
import TextField from './TextField'
import Dialog, { DialogButtonRow } from './Dialog'
import { LetterPreview, toPdfUrl } from './SettlementMessageDialog'
import type { RankedRestaurant, RestaurantSettlementPotential } from './RestaurantRanking'
import { DEVELOPMENT } from '../consts'
import { handleMutationError } from '../utils/errors'
import createRestaurantMessage, {
	RestaurantMessage,
	RestaurantMessageAttachment,
	RestaurantMessageContent,
} from '../mutations/createRestaurantMessage'
import sendMessage from '../mutations/sendMessage'

type LetterFormat = 'EMAIL' | 'PAPER'

// PREVIEW ist eine Vorlage für den Freitext; an die API geht sie als FREE_TEXT.
type DialogContent = RestaurantMessageContent | 'PREVIEW'

const CONTENT_ITEMS: Record<LetterFormat, { label: string; value: DialogContent }[]> = {
	PAPER: [
		{ label: 'Freitext', value: 'FREE_TEXT' },
		{ label: 'Initialer Brief', value: 'INITIAL' },
	],
	EMAIL: [
		{ label: 'Freitext', value: 'FREE_TEXT' },
		{ label: 'Initiale Mail', value: 'INITIAL' },
		{ label: 'Vorschau bestätigen', value: 'PREVIEW' },
	],
}

// Nach der mündlichen Zusage: erklärt die Vorschau ohne Vertrag und bittet um Bestätigung per Antwort.
const getPreviewSubject = (restaurantName: string): string =>
	`${restaurantName} in der Mitbestellt-App – Vorschau, bitte kurz bestätigen`

const getPreviewIntro = (restaurantName: string): string =>
	`vielen Dank für das nette Gespräch! Wie besprochen möchten wir ${restaurantName} schon jetzt als Vorschau in der Mitbestellt-App zeigen, also bevor der Vertrag unterschrieben ist. Damit es keine Missverständnisse gibt, hier kurz, was das bedeutet:

– Sichtbar: Kunden in der App sehen Ihr Restaurant mit Ihrer Speisekarte. Die Karte haben wir aus Ihrer Vorlage übernommen.
– Klarer Hinweis: Wer Ihr Restaurant antippt, bekommt einen Hinweis: Das ist eine Vorschau, das Restaurant ist noch nicht vollständig an Mitbestellt angebunden.
– Bestellungen werden gesammelt, nicht weitergeleitet: Kunden können bestellen. Die Bestellungen gehen aber noch nicht an Sie, und Sie müssen nichts annehmen oder liefern. Für jede Bestellung bekommen Sie eine anonyme Mail mit Ort, Bestellsumme und Anzahl der Personen. Zusätzlich bekommen Sie einmal am Tag eine kurze Übersicht, wie viele Leute sich Ihr Restaurant angesehen haben.
– Was mit den Bestellungen passiert: Jede Bestellung wartet bis 10 Minuten nach Ende ihres Bestellzeitraums. Sind Sie bis dahin nicht angebunden, verfällt sie automatisch, und der Kunde wird informiert. Für den Kunden entstehen keine Kosten.
– Für Sie kostenlos und unverbindlich: Die Vorschau kostet Sie nichts und verpflichtet Sie zu nichts. Sie können sie jederzeit beenden, eine kurze Nachricht an mich genügt.
– Echte Bestellungen bekommen: Dafür unterschreiben Sie den Vertrag und bestätigen Ihre Speisekarte, vor allem die Angaben zu Allergenen. Ab dann kommen die Bestellungen direkt bei Ihnen an.

Bitte bestätigen Sie mir kurz per Antwort auf diese Mail, dass Sie damit einverstanden sind, dass wir ${restaurantName} mit Name, Adresse und Speisekarte als Vorschau in der App zeigen.

Bei Fragen erreichen Sie mich jederzeit per Mail oder Telefon.`

const ATTACHMENT_ITEMS: { id: RestaurantMessageAttachment; label: string }[] = [
	{ id: 'DV', label: 'Dienstleistungsvertrag (DV)' },
	{ id: 'HAGB', label: 'Händler-AGB (HAGB)' },
]

const DIALOG_WIDTH = '64rem'

// Fast fensterfüllend, damit sich der Brief in der Vorschau lesen lässt wie ausgedruckt.
const LETTER_DIALOG_WIDTH = '95vw'

// Links die Mail so breit wie im Postfach (560 px plus Rand) bzw. der Brief, rechts Adresse und Gemeinden.
const sxColumns = { display: 'flex', gap: '1.5rem', alignItems: 'stretch' } as const

// Oben etwas Luft, sonst schneidet die scrollende Spalte die Beschriftung des ersten Felds ab.
const sxSide = { flex: '0 0 20rem', height: '70vh', overflowY: 'auto', pt: '0.5rem' } as const

// Dieselbe Anrede, die die API ohne Angabe setzt; Inhabernamen kennen wir vor dem Onboarding nicht.
const DEFAULT_GREETING: Record<LetterFormat, string> = {
	PAPER: 'Sehr geehrte Damen und Herren,',
	EMAIL: 'Guten Tag,',
}

const DEFAULT_INTRO =
	'vielen Dank für das nette Gespräch! Wie besprochen schicke ich Ihnen hier die Details zu Mitbestellt.'

// So lange nach dem letzten Tastendruck wird die Vorschau mit dem neuen Betreff und Anschreiben erzeugt.
const TEXT_PREVIEW_DELAY_MS = 400

const sxPreview = {
	display: 'block',
	flex: '1',
	minWidth: '0',
	height: '70vh',
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '0.5rem',
} as const

const sxRow = {
	display: 'flex',
	gap: '0.5rem',
	alignItems: 'center',
	p: '0.25rem 0',
	borderBottom: '1px solid',
	borderColor: 'divider',
} as const

// Ausgegraut: eine einzelne Lieferando-Bestellung zum MBW bringt dem Restaurant dort Verlust.
const sxUnprofitableRow = { ...sxRow, opacity: 0.4 } as const

const formatEuro = (value: number): string => `${Math.round(value).toLocaleString('de-DE')} €`

const SettlementRow = React.memo(
	({
		potential,
		checked,
		onToggle,
	}: {
		potential: RestaurantSettlementPotential
		checked: boolean
		onToggle: (nextValue: boolean, id?: string) => void
	}): JSX.Element => (
		<Box
			sx={
				potential.singleOrderMarginShare !== null && potential.singleOrderMarginShare < 0
					? sxUnprofitableRow
					: sxRow
			}
		>
			<Box flex="1" minWidth="0">
				<CheckBox
					id={potential.settlementId}
					label={potential.settlementName}
					defaultValue={checked}
					onChange={onToggle}
				/>
				<Txt
					variant="caption"
					color="text.secondary"
					noWrap
					title="DB: Deckungsbeitrag einer einzelnen, selbst ausgefahrenen Lieferando-Bestellung zum MBW"
				>
					MBW {potential.minOrderValue === null ? '—' : formatEuro(potential.minOrderValue)} · DB{' '}
					{potential.singleOrderMarginShare === null
						? '—'
						: `${Math.round(potential.singleOrderMarginShare * 100)} %`}{' '}
					· {formatEuro(potential.monthlyPlatformRevenue)} Mitbestellt/Mon.
				</Txt>
			</Box>
		</Box>
	)
)
SettlementRow.displayName = 'SettlementRow'

const RestaurantMessageDialog = ({
	restaurant,
	format,
	dismissPortal,
}: {
	restaurant: RankedRestaurant
	format: LetterFormat
	dismissPortal: () => void
}): JSX.Element => {
	const [email, setEmail] = React.useState(restaurant.email ?? '')
	// Briefe schickt die API lokal ohnehin nur im Testmodus, in Produktion lehnt sie die Sandbox ab.
	const [sandbox, setSandbox] = React.useState(format === 'PAPER' && DEVELOPMENT)
	// Freitext trägt nur Betreff, Anrede und Anschreiben; der initiale Brief zeigt ohne sie die Gemeinden.
	const [content, setContent] = React.useState<DialogContent>('FREE_TEXT')
	const apiContent: RestaurantMessageContent = content === 'INITIAL' ? 'INITIAL' : 'FREE_TEXT'
	const [subject, setSubject] = React.useState('')
	const [greeting, setGreeting] = React.useState(DEFAULT_GREETING[format])
	const [intro, setIntro] = React.useState(DEFAULT_INTRO)
	// Die Vorlage füllt Betreff und Anschreiben neu, der Freitext stellt die Vorgaben wieder her.
	const handleChangeContent = React.useCallback(
		(value: string | null) => {
			if (value === 'INITIAL' || value === 'FREE_TEXT' || value === 'PREVIEW') setContent(value)
			if (value === 'PREVIEW') {
				setSubject(getPreviewSubject(restaurant.name))
				setIntro(getPreviewIntro(restaurant.name))
			} else if (value === 'FREE_TEXT') {
				setSubject('')
				setIntro(DEFAULT_INTRO)
			}
		},
		[restaurant.name]
	)
	const [previewTexts, setPreviewTexts] = React.useState({
		subject: '',
		greeting: DEFAULT_GREETING[format],
		intro: DEFAULT_INTRO,
	})
	React.useEffect(() => {
		const timeout = setTimeout(
			() => setPreviewTexts({ subject, greeting, intro }),
			TEXT_PREVIEW_DELAY_MS
		)
		return () => clearTimeout(timeout)
	}, [subject, greeting, intro])
	const [attachments, setAttachments] = React.useState<RestaurantMessageAttachment[]>([])
	const handleToggleAttachment = React.useCallback((nextValue: boolean, id?: string) => {
		setAttachments((previousAttachments) => [
			...previousAttachments.filter((attachment) => attachment !== id),
			...ATTACHMENT_ITEMS.filter((item) => nextValue && item.id === id).map((item) => item.id),
		])
	}, [])
	// Nach Deckungsbeitrag absteigend; ohne Fahrzeit unbekannt und ans Ende.
	const settlements = React.useMemo(
		() =>
			[...restaurant.settlements].sort(
				(a, b) =>
					(b.singleOrderMarginShare ?? -Infinity) - (a.singleOrderMarginShare ?? -Infinity) ||
					b.monthlyPlatformRevenue - a.monthlyPlatformRevenue
			),
		[restaurant.settlements]
	)
	// Vorab die erste Gemeinde ohne Verlust, damit die Vorschau gleich etwas zeigt.
	const [checkedIds, setCheckedIds] = React.useState<string[]>(
		settlements
			.filter(({ singleOrderMarginShare }) => (singleOrderMarginShare ?? 0) >= 0)
			.slice(0, 1)
			.map(({ settlementId }) => settlementId)
	)
	// In der Reihenfolge der Liste, also nach Deckungsbeitrag; so nennt sie auch der Text.
	const settlementIds = React.useMemo(
		() =>
			settlements
				.map(({ settlementId }) => settlementId)
				.filter((settlementId) => checkedIds.includes(settlementId)),
		[checkedIds, settlements]
	)
	const [mail, setMail] = React.useState<RestaurantMessage | undefined>(undefined)
	const [sending, setSending] = React.useState(false)
	const [result, setResult] = React.useState<{ sent: boolean; message: string } | undefined>(
		undefined
	)

	// Nach jedem Umschalten und Tippen neu; bis die Antwort da ist, bleibt die alte Vorschau stehen,
	// und eine überholte Antwort ersetzt sie nicht mehr.
	// Die Anlagen hängen nur am Brief mit in der Vorschau, die Mail zeigt sie nicht.
	React.useEffect(() => {
		let outdated = false
		const loadMail = async () => {
			const response = await createRestaurantMessage({
				settlementIds: apiContent === 'INITIAL' ? settlementIds : [],
				slug: restaurant.slug,
				format,
				intro: apiContent === 'FREE_TEXT' ? previewTexts.intro : undefined,
				greeting: apiContent === 'FREE_TEXT' ? previewTexts.greeting : undefined,
				content: apiContent,
				subject: previewTexts.subject,
				attachments: format === 'PAPER' ? attachments : undefined,
			})
			if (outdated) {
				return
			} else if (response.success) {
				setMail(response.message)
			} else {
				handleMutationError('createRestaurantMessage', response.errors)
			}
		}
		if (apiContent === 'FREE_TEXT' || settlementIds.length) loadMail()
		return () => {
			outdated = true
		}
	}, [apiContent, attachments, format, previewTexts, restaurant.slug, settlementIds])

	const handleToggle = React.useCallback((nextValue: boolean, id?: string) => {
		setCheckedIds((previousIds) => [
			...previousIds.filter((checkedId) => checkedId !== id),
			...(nextValue && id !== undefined ? [id] : []),
		])
	}, [])

	const handleDownload = React.useCallback(() => {
		if (mail?.pdf) {
			const url = toPdfUrl(mail.pdf)
			const link = document.createElement('a')
			link.href = url
			link.download = `Mitbestellt-Brief-${restaurant.name}.pdf`
			link.click()
			URL.revokeObjectURL(url)
		}
	}, [mail, restaurant.name])

	const handleSend = React.useCallback(async () => {
		setSending(true)
		const response = await sendMessage({
			settlementIds: apiContent === 'INITIAL' ? settlementIds : [],
			slugs: [restaurant.slug],
			sandbox,
			format,
			email: format === 'EMAIL' ? email.trim() : undefined,
			intro: apiContent === 'FREE_TEXT' ? intro : undefined,
			greeting: apiContent === 'FREE_TEXT' ? greeting : undefined,
			content: apiContent,
			subject,
			attachments,
		})
		setSending(false)
		if (response.success && !response.results[0]?.error) {
			setResult({
				sent: true,
				message:
					format === 'EMAIL'
						? `Gesendet an ${email.trim()}.`
						: `${sandbox ? 'Sandbox: ' : ''}an LetterXpress übergeben · Auftrag ${
								response.results[0]?.reference
						  }`,
			})
		} else {
			setResult({
				sent: false,
				message: response.success
					? response.results[0]?.error ?? 'Versand fehlgeschlagen'
					: typeof response.errors === 'string'
					? response.errors
					: 'Versand fehlgeschlagen',
			})
		}
	}, [
		apiContent,
		attachments,
		email,
		format,
		greeting,
		intro,
		restaurant.slug,
		sandbox,
		settlementIds,
		subject,
	])

	const showsPreview = apiContent === 'FREE_TEXT' || settlementIds.length > 0

	return (
		<Box p="1.5rem" width={format === 'PAPER' ? LETTER_DIALOG_WIDTH : DIALOG_WIDTH} maxWidth="100%">
			<Txt fontWeight="bold" variant="h5" m="0 0 0.25rem 0">
				{format === 'PAPER' ? 'Brief' : 'Mail'} an {restaurant.name}
			</Txt>
			<Txt variant="body2" color="text.secondary" m="0 0 1rem 0">
				{!showsPreview
					? 'Mindestens eine Gemeinde auswählen'
					: format === 'PAPER'
					? content === 'INITIAL'
						? 'Per LetterXpress, je Gemeinde die erreichbaren Haushalte'
						: 'Per LetterXpress, die Anlagen hängen als weitere Seiten an'
					: mail
					? mail.subject
					: 'Vorschau wird erzeugt …'}
			</Txt>
			<Box sx={sxColumns}>
				{format === 'PAPER' ? (
					<LetterPreview pdf={showsPreview ? mail?.pdf ?? undefined : undefined} />
				) : mail && showsPreview ? (
					<Box
						component="iframe"
						sx={sxPreview}
						srcDoc={mail.html ?? undefined}
						title="Vorschau der Mail"
					/>
				) : (
					<Box sx={sxPreview} />
				)}
				<Box sx={sxSide}>
					<SelectField
						label="Inhalt"
						width="100%"
						items={CONTENT_ITEMS[format]}
						defaultValue={content}
						onChange={handleChangeContent}
						m="0 0 1rem 0"
					/>
					{format === 'EMAIL' ? (
						<TextField
							label="E-Mail des Restaurants"
							defaultValue={restaurant.email ?? ''}
							onChange={setEmail}
							m="0 0 1rem 0"
						/>
					) : null}
					{/* Der Key mountet die Felder neu, wenn die Vorlage Betreff und Anschreiben ersetzt. */}
					{apiContent === 'FREE_TEXT' ? (
						<React.Fragment key={content}>
							<TextField
								label={format === 'EMAIL' ? 'Betreff' : 'Betreff (optional)'}
								defaultValue={subject}
								onChange={setSubject}
								m="0 0 1rem 0"
							/>
							<TextField
								label="Anrede"
								defaultValue={greeting}
								onChange={setGreeting}
								m="0 0 1rem 0"
							/>
							<TextField
								label="Anschreiben"
								defaultValue={intro}
								onChange={setIntro}
								multiline
								maxRows={16}
								m="0 0 1rem 0"
							/>
						</React.Fragment>
					) : null}
					{content === 'INITIAL' ? (
						<>
							<Txt variant="subtitle2" m="0 0 0.25rem 0">
								Bezug auf Gemeinden
							</Txt>
							{settlements.map((potential) => (
								<SettlementRow
									key={potential.settlementId}
									potential={potential}
									checked={checkedIds.includes(potential.settlementId)}
									onToggle={handleToggle}
								/>
							))}
						</>
					) : null}
					<Txt variant="subtitle2" m="1rem 0 0.25rem 0">
						Anlagen
					</Txt>
					{ATTACHMENT_ITEMS.map((item) => (
						<CheckBox
							key={item.id}
							id={item.id}
							label={item.label}
							defaultValue={attachments.includes(item.id)}
							onChange={handleToggleAttachment}
						/>
					))}
					{result ? (
						<Txt
							variant="body2"
							color={result.sent ? 'success.main' : 'error.main'}
							m="0.75rem 0 0 0"
						>
							{result.message}
						</Txt>
					) : null}
				</Box>
			</Box>
			<DialogButtonRow>
				{format === 'PAPER' ? (
					<>
						<CheckBox
							label={
								DEVELOPMENT
									? 'Sandbox (lokal immer, nichts wird gedruckt)'
									: 'Sandbox (nur LetterXpress-Postbox, nichts wird gedruckt)'
							}
							defaultValue={sandbox}
							disabled={DEVELOPMENT}
							onChange={setSandbox}
						/>
						<Button m="0 0 0 1rem" onClick={handleDownload} disabled={!mail?.pdf}>
							PDF herunterladen
						</Button>
					</>
				) : null}
				{/* Aus der Sandbox darf man erneut senden, nach einem echten Versand nicht. */}
				<Button
					variant="mainButton"
					m="0 0 0 1rem"
					onClick={handleSend}
					loading={sending}
					disabled={
						!mail ||
						!showsPreview ||
						(format === 'EMAIL' && !email.trim()) ||
						(apiContent === 'FREE_TEXT' && !intro.trim()) ||
						(apiContent === 'FREE_TEXT' && format === 'EMAIL' && !subject.trim()) ||
						(result?.sent && !sandbox)
					}
				>
					Senden
				</Button>
				<Button m="0 0 0 1rem" onClick={dismissPortal}>
					Schließen
				</Button>
			</DialogButtonRow>
		</Box>
	)
}

// Mail oder Brief per LetterXpress an ein Restaurant, als Freitext oder initial mit Gemeinden:
// Vorschau ansehen, an die Adresse aus dem Telefonat bzw. die Anschrift des Restaurants senden.
export const renderRestaurantMessageDialog = (
	restaurant: RankedRestaurant,
	format: LetterFormat
): void =>
	Dialog.render({
		width: format === 'PAPER' ? LETTER_DIALOG_WIDTH : DIALOG_WIDTH,
		isLocked: true,
		renderContent: ({ dismissPortal }) => (
			<RestaurantMessageDialog
				restaurant={restaurant}
				format={format}
				dismissPortal={dismissPortal}
			/>
		),
	})
